import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { capitalizeString } from 'src/utils/string';
import AccountSearchIcon from '../../assets/AccountSearchIcon';
import ErrorIcon from '../../assets/ErrorIcon';
import HelpIcon from '../../assets/HelpIcon';
import SearchIcon from '../../assets/SearchIcon';
import type { FirstNameDataObject } from '../../reducers/firstNameReducer';
import { firstNameActions } from '../../reducers/firstNameReducer';
import type { LastNameDataObject } from '../../reducers/lastNameReducer';
import { lastNameActions } from '../../reducers/lastNameReducer';
import type { SearchReducerState } from '../../reducers/searchReducer';
import { actions as searchActions } from '../../reducers/searchReducer';
import type { RootState } from '../../store';
import type {
  DataWorldChartTypes,
  NameOccurrencesDataType,
} from '../../types/types';
import { allCountriesISO2ToName } from '../../utils/allCountries';
import { convertToUrl } from '../../utils/convertToUrl';
import { getCookie, setCookie } from '../../utils/cookies';
import { displayToast } from '../../utils/displayToast';
import { formatNumber } from '../../utils/format';
import { trackCustomEvent } from '../../utils/trackCustomEvents';
import BackButton from '../button/back-button';
import SearchForRecords from '../search-input/search-for-records';
import NameOccurrencesTable from '../table/name-occurrences-table';
import GoogleWorldMapChart from '../world-map-chart/world-map-chart';

interface PersonPageProps {
  countries: DataWorldChartTypes[];
  personNameDataProps: {
    countries: NameOccurrencesDataType[];
  };
  nameType: 'first' | 'last';
  personNameData: PersonNameDataObject[];
  hasDataForOppositeNameType: boolean;
  nameSuggestions: string[];
}

type PersonNameDataObject = LastNameDataObject | FirstNameDataObject;

export default function PersonPage({
  countries,
  personNameDataProps,
  nameType,
  personNameData,
  hasDataForOppositeNameType,
  nameSuggestions,
}: PersonPageProps) {
  const dispatch = useDispatch();

  const [viewTabOpened, setViewTabOpened] = useState<'table' | 'map'>('table');
  const handleViewTabOpened = (tab: 'table' | 'map') => () => {
    setViewTabOpened(tab);
  };

  const hasData = !isEmpty(personNameDataProps.countries);

  const searchFilterCountries = useMemo(() => {
    return countries?.filter((country) =>
      personNameDataProps.countries?.some(
        (countryData) => countryData.iso_3 === country.iso_3
      )
    );
  }, [countries, personNameDataProps]);

  const tableData = useMemo(() => {
    if (
      !countries ||
      !countries?.length ||
      !Array.isArray(countries) ||
      !personNameDataProps ||
      !personNameDataProps.countries?.length
    ) {
      return [];
    }
    const { countries: countriesForPerson } = personNameDataProps;

    return countriesForPerson.map((countryData: NameOccurrencesDataType) => {
      const country: DataWorldChartTypes = countries.find(
        (country) => country.iso_3 === countryData.iso_3
      ) as DataWorldChartTypes;
      return {
        ...countryData,
        name: country.name,
        living: country.living,
        ID: country.ID,
        iso_2: country.iso_2,
      };
    });
  }, [countries, personNameDataProps]);

  const router = useRouter();
  const querySlug =
    nameType === 'first'
      ? router.query.firstname_slug
      : router.query.lastname_slug;
  const personName = ((querySlug
    ?.toString()
    .charAt(0)
    .toUpperCase() as string) + querySlug?.toString().slice(1)) as string;

  const countryFromQuery = useMemo(() => {
    return allCountriesISO2ToName[
      (router.query.country as string)?.toUpperCase()
    ];
  }, [router?.query?.country]);

  const { country }: Pick<SearchReducerState, 'country'> = useSelector(
    ({ search: { country } }: RootState) => ({
      country,
    })
  );

  const copyOfPersonNameData = personNameData ? [...personNameData] : [];

  const sortedPersonNameData = copyOfPersonNameData?.sort(
    (a: { count: number }, b: { count: number }) => {
      return b.count - a.count;
    }
  );

  const completeArrayOfPersonNameData: PersonNameDataObject[][] = [];

  sortedPersonNameData?.forEach(
    (x: PersonNameDataObject, y: number, z: PersonNameDataObject[]) => {
      return !(y % 10)
        ? completeArrayOfPersonNameData?.push(z.slice(y, y + 10))
        : '';
    }
  );

  let numberOfHumansFound = 0;

  personNameData.forEach((data: PersonNameDataObject) => {
    numberOfHumansFound = numberOfHumansFound + data.count;
  });

  useEffect(() => {
    if (!isEmpty(personNameDataProps)) {
      const result = personNameDataProps?.countries;
      if (nameType === 'first') {
        dispatch(firstNameActions.setFirstNameData({ firstNameData: result }));
      } else {
        dispatch(lastNameActions.setLastNameData({ lastNameData: result }));
      }
    }
  }, []);

  const routeToLastCountry = () => {
    if (countryFromQuery) {
      return `/country/${convertToUrl(countryFromQuery)}`;
    }

    return '/world';
  };

  const data = personNameData;

  const dataConvertedToArray = useMemo(() => {
    let chartArray: unknown[][] = [];

    chartArray = [
      [
        'Country',
        'Population Listed',
        { role: 'tooltip', type: 'string', p: { html: true } },
      ],
    ];
    data?.forEach((item: { count: number; iso_3: string }) => {
      const iso2 = [...(countries as DataWorldChartTypes[])].find(
        (countryFromState: DataWorldChartTypes) => {
          return countryFromState.iso_3 === item.iso_3;
        }
      )?.iso_2;
      const countryName = (countries as DataWorldChartTypes[]).find(
        (countryFromState: DataWorldChartTypes) => {
          return countryFromState.iso_3 === item.iso_3;
        }
      )?.name;
      chartArray.push([
        iso2,
        item.count,
        '<div>' + countryName + '<br />' + formatNumber(item.count) + '</div>',
      ]);
    });
    return chartArray;
  }, [JSON.stringify(data), JSON.stringify(countries)]);

  const gcoptions = {
    colorAxis: {
      colors: ['#7ADDC1', '#409F85'],
    },
    tooltip: { isHtml: true, trigger: 'visible' },
    region: 'world',
    legend: 'hidden',
  };

  const { t } = useTranslation(`${nameType}-name-slug`);

  const _renderPageHeader = () => {
    return (
      <div className="flex flex-col justify-between tablet:mt-[16px] tablet:flex-row">
        <div className="flex flex-col justify-start">
          <p className="text-[14px] text-[#3F4753]">
            {`${
              nameType.charAt(0).toUpperCase() + nameType.slice(1)
            } name results for:`}
          </p>
          <p className="text-[18px] text-slateBlack md:text-[28px]">
            <b>{personName}</b>&nbsp;({formatNumber(numberOfHumansFound)})
          </p>
        </div>
        {hasDataForOppositeNameType ? (
          <Link
            href={`/${
              nameType === 'first' ? 'last' : 'first'
            }-name/${convertToUrl(personName)}`}
            className="mt-[16px] flex flex-row items-end pr-[8px] tablet:mt-0 tablet:px-[8px]"
          >
            <AccountSearchIcon className="mr-[8px]" />
            <p className="text-[14px] text-[#3F4753] tablet:text-[16px]">
              {`Show results for the ${
                nameType === 'first' ? 'last' : 'first'
              } name `}
              &nbsp;
              <b className="text-[14px] text-[#266867] underline tablet:text-[16px]">
                {personName}
              </b>
            </p>
          </Link>
        ) : null}
      </div>
    );
  };

  const _renderResultsByCountry = () => {
    return (
      <div className="mb-[40px] flex flex-col">
        <div className="mb-[16px] mt-[24px] flex flex-col items-start justify-between md:flex-row md:items-center">
          <p className="mb-[8px] text-[16px] font-semibold text-slateBlack md:mb-0 md:text-[18px]">
            Results by Country
          </p>
          <div className="flex w-full flex-row items-center rounded-lg bg-[#E7E8E9] p-[4px] md:w-max">
            <button
              onClick={handleViewTabOpened('table')}
              className={`flex-1 cursor-pointer px-[24px] py-[8px] text-[14px] font-medium md:min-w-[125px] ${
                viewTabOpened === 'table'
                  ? 'bg-white shadow-[0_1px_4px_0_rgba(15,25,40,0.08)]'
                  : 'text-[#6F757E]'
              } rounded-lg`}
            >
              Table View
            </button>
            <button
              onClick={handleViewTabOpened('map')}
              className={`flex-1 cursor-pointer px-[24px] py-[8px] text-[14px] font-medium md:min-w-[125px] ${
                viewTabOpened === 'map'
                  ? 'bg-white shadow-[0_1px_4px_0_rgba(15,25,40,0.08)]'
                  : 'text-[#6F757E]'
              } rounded-lg`}
            >
              Map View
            </button>
          </div>
        </div>
        <div
          className={`flex flex-col ${
            viewTabOpened === 'table' ? 'block' : 'hidden'
          }`}
        >
          <NameOccurrencesTable countries={tableData} />
        </div>
        <div
          className={`flex min-h-[300px] overflow-x-auto flex-col items-center justify-center rounded-lg border border-[#E7E8E9] bg-white p-[32px] shadow-[0px_4px_14px_0px_rgba(39,48,62,0.03)] md:min-h-[420px] tablet:mt-[40px] xl:min-h-[680px] ${
            viewTabOpened !== 'table' ? 'block' : 'hidden'
          }`}
          key={`world-map-chart-mobile`}
        >
          <GoogleWorldMapChart
            worldChartWidth={0}
            data={dataConvertedToArray}
            gcoptions={gcoptions}
            namePage={true}
          />
        </div>
      </div>
    );
  };

  const suggestionSearchWrapper = (suggestion: string, index: number) => {
    return (
      <Link
        href={getSuggestionUrl(suggestion)}
        onClick={() => handleClickSuggestion(suggestion)}
        key={suggestion}
        className={`flex flex-row items-center rounded-lg border border-[#E7E8E9] px-[16px] py-[20px] hover:border-[#409F85]
          ${[6, 7]?.includes(index) ? ' tablet:hidden xl:!flex' : ''}`}
      >
        {nameType === 'first' ? (
          <>
            <SearchIcon fill="#9FA3A9" width={24} height={24} />{' '}
            <p className="text-left text-[14px] capitalize">
              {personName}&nbsp;<b>{suggestion}</b>
            </p>
          </>
        ) : (
          <>
            <SearchIcon fill="#9FA3A9" width={24} height={24} />{' '}
            <p className="text-left text-[15px]">
              <b>{suggestion}</b>&nbsp;{personName}
            </p>
          </>
        )}
      </Link>
    );
  };

  const mostVisitedCountriesIds = getCookie(
    'mostVisitedCountriesIds'
  ) as string;
  const recentSearches = getCookie('recentSearches') as string;

  const userCountry = useMemo(() => {
    let countryName = countryFromQuery || 'United States of America';
    if (!countryName || countryName?.length === 0) {
      if (recentSearches?.length) {
        const recentSearchesArray = JSON.parse(recentSearches as string);

        countryName = recentSearchesArray[0]?.country;
      } else {
        if (mostVisitedCountriesIds?.length) {
          const mostVisitedCountriesIdsArray = JSON.parse(
            mostVisitedCountriesIds as string
          );

          return countries.find(
            (country) => country.ID === mostVisitedCountriesIdsArray[0]
          );
        }
      }
    }

    return countries.find(
      (country) => country?.name?.toLowerCase() === countryName?.toLowerCase()
    );
  }, [countries, mostVisitedCountriesIds, recentSearches, countryFromQuery]);

  const { defaultNrOfResults } = useSelector((state: RootState) => state.user);

  const getSuggestionUrl = (suggestion: string) => {
    return `/search?firstName=${
      nameType === 'first' ? personName : suggestion
    }&lastName=${nameType === 'last' ? personName : suggestion}&country=${
      userCountry?.iso_2 || 'US'
    }&page=1&pageSize=${defaultNrOfResults}`;
  };

  const handleClickSuggestion = (suggestion: string) => {
    if (personName?.length === 0 || suggestion?.length === 0 || !userCountry) {
      displayToast(t('please-select'), 'error', <ErrorIcon />);
      return;
    }

    if (mostVisitedCountriesIds) {
      if (userCountry) {
        const newMostVisitedCountriesIds = [
          ...JSON.parse(mostVisitedCountriesIds as string),
        ];

        newMostVisitedCountriesIds.unshift(userCountry.ID);

        const filteredMostVisitedCountriesIds = [
          ...new Set(newMostVisitedCountriesIds),
        ];

        if (newMostVisitedCountriesIds?.length < 4) {
          setCookie(
            'mostVisitedCountriesIds',
            JSON.stringify(filteredMostVisitedCountriesIds)
          );
        } else {
          setCookie(
            'mostVisitedCountriesIds',
            JSON.stringify(filteredMostVisitedCountriesIds.slice(0, 3))
          );
        }
      }
    } else {
      setCookie(
        'mostVisitedCountriesIds',
        JSON.stringify([(userCountry as DataWorldChartTypes)?.ID])
      );
    }

    if (userCountry && personName?.length > 0 && suggestion?.length > 0) {
      dispatch(
        searchActions.setIsLoadingSearchResults({
          isLoadingSearchResults: true,
        })
      );

      dispatch(
        searchActions.setFirstName({
          first_name: nameType === 'first' ? personName : suggestion,
        })
      );

      dispatch(
        searchActions.setLastName({
          last_name: nameType === 'last' ? personName : suggestion,
        })
      );

      trackCustomEvent(
        'Search from search bar',
        `${nameType === 'first' ? personName : suggestion} ${
          nameType === 'last' ? personName : suggestion
        } - ${userCountry?.name}`
      );

      if (recentSearches) {
        const search = {
          country: userCountry?.name,
          firstName: nameType === 'first' ? personName : suggestion,
          lastName: nameType === 'last' ? personName : suggestion,
        };

        const newRecentSearches = [...JSON.parse(recentSearches as string)];

        newRecentSearches.unshift(search);

        const filteredRecentSearches = newRecentSearches.filter(
          (search, index, self) =>
            index ===
            self.findIndex(
              (t) =>
                t.country === search.country &&
                t.firstName === search.firstName &&
                t.lastName === search.lastName
            )
        );

        if (filteredRecentSearches?.length <= 5) {
          setCookie('recentSearches', JSON.stringify(filteredRecentSearches));
        } else {
          const newfilteredRecentSearches = filteredRecentSearches?.slice(0, 5);

          setCookie(
            'recentSearches',
            JSON.stringify(newfilteredRecentSearches)
          );
        }
      } else {
        const search = {
          country: userCountry?.name,
          firstName: nameType === 'first' ? personName : suggestion,
          lastName: nameType === 'last' ? personName : suggestion,
        };

        setCookie('recentSearches', JSON.stringify([search]));
      }
    }

    dispatch(
      searchActions.setIsSearchTriggeredWithFilters({
        isSearchTriggeredWithFilters: false,
      })
    );
  };

  // const handleRedirectToCreatePage = (
  //   event: React.MouseEvent<HTMLAnchorElement>
  // ) => {
  //   event.preventDefault();
  //   localStorage.setItem(
  //     'firstName',
  //     String(capitalizeString(router.query?.firstname_slug?.toString() ?? ''))
  //   );
  //   localStorage.setItem(
  //     'lastName',
  //     String(capitalizeString(router.query?.lastname_slug?.toString() ?? ''))
  //   );
  //   router.push('/create-human');
  // };

  const _renderSearchPerson = () => {
    return (
      <div
        className="mb-[80px] flex flex-col rounded-lg border border-solid border-[#E7E8E9] bg-white
          p-[32px] shadow-[0px_4px_14px_0px_rgba(39,48,62,0.03)]"
      >
        <SearchForRecords
          defaultValues={{
            firstName: nameType === 'first' ? personName : '',
            lastName: nameType === 'last' ? personName : '',
          }}
          countries={searchFilterCountries}
          userCountry={userCountry?.name}
        />
        {/* <Link
          href="/create-human"
          onClick={handleRedirectToCreatePage}
          className="mt-2 flex w-fit flex-row items-center py-3"
        >
          <HelpIcon className="mb-px" />
          <p className="ml-[12px] text-[12px] font-medium text-[#6F757E] md:text-[14px]">
            {t('cannot-find')}&nbsp;
            <b className="font-semibold text-[#266867] underline">
              {t('create-it')}
            </b>
          </p>
        </Link> */}
        <div className="mt-[32px]">
          <p className="text-[16px] font-medium">{t('users-alt-searching')}</p>
          <div className="mt-[16px] grid grid-cols-1 gap-[16px] md:grid-cols-2 tablet:grid-cols-3 xl:!grid-cols-4">
            {nameSuggestions?.map((suggestion, index) =>
              suggestionSearchWrapper(suggestion, index)
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`flex grow flex-col bg-[#FAFAFA] px-[16px] md:px-[48px]`}>
      <BackButton
        url={routeToLastCountry()}
        label="Back"
        forceGoBack={!!countryFromQuery}
      />
      {hasData ? (
        <>
          {_renderPageHeader()}
          {_renderResultsByCountry()}
          {_renderSearchPerson()}
        </>
      ) : (
        <div className="m-auto whitespace-pre-line py-10 text-center text-[#3F4753]">
          {t('person:not-found', { personName })}
        </div>
      )}
    </div>
  );
}
